import { motion } from 'framer-motion'
import { Mail, Phone, MapPin, Send } from 'lucide-react'

interface ContactSectionProps {
  onCTAClick: () => void
}

const contactInfo = [
  { icon: Mail, label: 'Email', value: 'info@softrest.ru', href: 'mailto:info@softrest.ru' },
  { icon: Phone, label: 'Телефон', value: '+7 (929) 173-55-65', href: 'tel:+79291735565' },
  { icon: MapPin, label: 'Локация', value: 'Россия', href: null }
]

export default function ContactSection({ onCTAClick }: ContactSectionProps) {
  return (
    <section className="py-24 bg-gradient-to-br from-blue-600 via-indigo-600 to-purple-700 text-white relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-20 left-20 w-64 h-64 bg-white rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-20 w-96 h-96 bg-white rounded-full blur-3xl" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left content */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-4xl lg:text-5xl font-bold mb-6">
              Готовы обсудить ваш проект?
            </h2>
            <p className="text-xl text-blue-100 mb-8">
              Оставьте заявку, и мы ответим в течение 1 рабочего дня с предварительной оценкой и планом работ
            </p>

            <div className="space-y-4 mb-8">
              {contactInfo.map((contact, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1, duration: 0.5 }}
                  className="flex items-center gap-4"
                >
                  <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
                    <contact.icon className="w-6 h-6" />
                  </div>
                  <div>
                    <div className="text-sm text-blue-200">{contact.label}</div>
                    {contact.href ? (
                      <a href={contact.href} className="text-lg font-semibold hover:text-blue-200 transition-colors">
                        {contact.value}
                      </a>
                    ) : (
                      <div className="text-lg font-semibold">{contact.value}</div>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>

            <motion.button
              onClick={onCTAClick}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="open-modal__btn inline-flex items-center gap-3 px-8 py-4 bg-white text-blue-600 rounded-xl font-semibold hover:bg-blue-50 transition-colors shadow-xl"
            >
              <Send className="w-5 h-5" />
              Получить консультацию
            </motion.button>
          </motion.div>

          {/* Right illustration */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="hidden lg:block"
          >
            <div className="bg-white/10 backdrop-blur-sm rounded-3xl p-8 border border-white/20">
              <div className="space-y-6">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-400 to-purple-500 rounded-full" />
                  <div className="flex-1">
                    <div className="h-4 bg-white/30 rounded w-3/4 mb-2" />
                    <div className="h-3 bg-white/20 rounded w-1/2" />
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 bg-gradient-to-br from-purple-400 to-pink-500 rounded-full" />
                  <div className="flex-1">
                    <div className="h-4 bg-white/30 rounded w-2/3 mb-2" />
                    <div className="h-3 bg-white/20 rounded w-3/5" />
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 bg-gradient-to-br from-pink-400 to-red-500 rounded-full" />
                  <div className="flex-1">
                    <div className="h-4 bg-white/30 rounded w-4/5 mb-2" />
                    <div className="h-3 bg-white/20 rounded w-2/3" />
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  )
}
